import React, { useState, useEffect, useMemo, memo } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    Box,
    CircularProgress,
    Alert,
    useTheme,
    alpha,
    Tooltip
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useWidgetState } from '@platform/core/src/context/WidgetStateContext';
import { useLookups } from '../../context/LookupsContext';
import { usePreferences } from '../../context/PreferencesContext';
import { addActivityToSession } from '../../services/workoutSessionApi';
import { useResourceOpener } from '@platform/core/src/hooks/useResourceOpener';
import { SpinWheelPicker } from '@platform/core/src/components/common';

const MEASUREMENT_RANGES = {
    WEIGHT: { min: 0, max: 1000, step: 2.5, default: 0 },
    REPETITIONS: { min: 0, max: 100, step: 1, default: 0 },
    RPE: { min: 0, max: 10, step: 0.5, default: 5 },
    DURATION: { min: 0, max: 300, step: 1, default: 0 },
    DISTANCE: { min: 0, max: 100, step: 0.1, default: 0 },
    SPEED: { min: 0, max: 25, step: 0.1, default: 0 },
    INCLINE: { min: -5, max: 30, step: 0.5, default: 0 },
    CALORIES: { min: 0, max: 5000, step: 5, default: 0 },
    HEART_RATE: { min: 40, max: 220, step: 1, default: 0 },
    DEFAULT: { min: 0, max: 500, step: 1, default: 0 }
};

// Purpose constants
const PURPOSE_WORKING = 'WORKING';
const PURPOSE_WARMUP = 'WARMUP';
const PURPOSE_COOLDOWN = 'COOLDOWN';

const PURPOSE_CYCLE = [PURPOSE_WORKING, PURPOSE_WARMUP, PURPOSE_COOLDOWN];

const generateOptions = (type) => {
    const t = type ? type.toUpperCase() : 'DEFAULT';
    let configKey = t;
    if (t === 'LBS' || t === 'KG') configKey = 'WEIGHT';
    if (t === 'REPS') configKey = 'REPETITIONS';
    if (t === 'MIN' || t === 'SEC') configKey = 'DURATION';
    
    const config = MEASUREMENT_RANGES[configKey] || MEASUREMENT_RANGES.DEFAULT;
    const { min, max, step } = config;
    const length = Math.floor((max - min) / step) + 1;
    
    return Array.from({ length }, (_, i) => {
        const val = min + (i * step);
        return step % 1 === 0 ? val : parseFloat(val.toFixed(2));
    });
};

const SpinWheelTrigger = ({ label, value, unit, onClick, disabled, isPlaceholder }) => {
  const theme = useTheme();
  
  // Determine display value: show "?" for unset placeholders
  const displayValue = isPlaceholder ? '--' : value;
  
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'baseline', sm: 'center' },
        justifyContent: 'center',
        gap: { xs: 0.5, sm: 0 },
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        px: { xs: 1.25, sm: 1.5 },
        py: { xs: 0.375, sm: 0.75 },
        borderRadius: { xs: 10, sm: 2 },
        bgcolor: isPlaceholder 
          ? alpha(theme.palette.warning.main, 0.06) 
          : 'background.paper',
        border: disabled ? '1px solid' : '2px dashed',
        borderColor: disabled 
          ? theme.palette.divider 
          : isPlaceholder 
            ? alpha(theme.palette.warning.main, 0.5) 
            : alpha(theme.palette.primary.main, 0.3),
        minWidth: { xs: 'auto', sm: '70px' },
        boxShadow: 0,
        opacity: disabled ? 0.7 : 1,
        // Subtle pulse animation for placeholder fields to draw attention
        ...(isPlaceholder && !disabled ? {
          animation: 'placeholderPulse 2s ease-in-out infinite',
          '@keyframes placeholderPulse': {
            '0%, 100%': { borderColor: alpha(theme.palette.warning.main, 0.35) },
            '50%': { borderColor: alpha(theme.palette.warning.main, 0.7) },
          },
        } : {}),
        '&:hover': {
          bgcolor: disabled 
            ? 'background.paper' 
            : isPlaceholder 
              ? alpha(theme.palette.warning.main, 0.12) 
              : 'action.hover',
          borderColor: disabled 
            ? theme.palette.divider 
            : isPlaceholder 
              ? alpha(theme.palette.warning.main, 0.7) 
              : alpha(theme.palette.primary.main, 0.5),
          transform: disabled ? 'none' : 'translateY(-1px)',
          boxShadow: disabled ? 0 : 2,
        },
        transition: 'all 0.2s',
      }}
    >
      {/* Desktop layout */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontWeight: 'bold',
            lineHeight: 1
          }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.25} sx={{ mt: 0.25 }}>
          <Typography 
            component="span" 
            sx={{ 
              fontSize: isPlaceholder ? '1rem' : '1.1rem', 
              fontWeight: 'bold', 
              color: disabled 
                ? 'text.disabled' 
                : isPlaceholder 
                  ? theme.palette.warning.main 
                  : 'primary.main', 
              lineHeight: 1,
              fontStyle: isPlaceholder ? 'italic' : 'normal',
            }}
          >
            {displayValue}
          </Typography>
          {unit && !isPlaceholder && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
              {unit}
            </Typography>
          )}
          {unit && isPlaceholder && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1, color: alpha(theme.palette.warning.main, 0.7) }}>
              {unit}
            </Typography>
          )}
        </Stack>
      </Box>
      
      {/* Mobile layout */}
      <Box
        component="span"
        sx={{
          display: { xs: 'inline-flex', sm: 'none' },
          alignItems: 'baseline',
          gap: 0.375
        }}
      >
        <Typography
          component="span"
          color="text.secondary"
          sx={{
            fontSize: '0.6rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 'bold'
          }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: disabled 
              ? 'text.disabled' 
              : isPlaceholder 
                ? theme.palette.warning.main 
                : 'primary.main',
            fontStyle: isPlaceholder ? 'italic' : 'normal',
          }}
        >
          {displayValue}
        </Typography>
        {unit && (
          <Typography
            component="span"
            sx={{ 
              fontSize: '0.6rem',
              color: isPlaceholder 
                ? alpha(theme.palette.warning.main, 0.7) 
                : 'text.secondary' 
            }}
          >
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Purpose Toggle Button - cycles through WORKING -> WARMUP -> COOLDOWN
 * Shows flame for warmup, snowflake for cooldown, faded dumbbell for working
 */
const PurposeToggle = ({ purpose, onChange, disabled }) => {
    const theme = useTheme();
    
    const handleClick = (e) => {
        e.stopPropagation();
        if (disabled) return;
        
        const currentIndex = PURPOSE_CYCLE.indexOf(purpose);
        const nextIndex = (currentIndex + 1) % PURPOSE_CYCLE.length;
        onChange(PURPOSE_CYCLE[nextIndex]);
    };
    
    const getPurposeConfig = () => {
        switch (purpose) {
            case PURPOSE_WARMUP:
                return {
                    icon: <LocalFireDepartmentIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />,
                    color: theme.palette.warning.main,
                    bgColor: alpha(theme.palette.warning.main, 0.15),
                    borderColor: alpha(theme.palette.warning.main, 0.4),
                    tooltip: 'Warmup Set - Click to change'
                };
            case PURPOSE_COOLDOWN:
                return {
                    icon: <AcUnitIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />,
                    color: theme.palette.info.main,
                    bgColor: alpha(theme.palette.info.main, 0.15),
                    borderColor: alpha(theme.palette.info.main, 0.4),
                    tooltip: 'Cooldown Set - Click to change'
                };
            case PURPOSE_WORKING:
            default:
                return {
                    icon: <FlexingBicepIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />,
                    color: theme.palette.text.disabled,
                    bgColor: 'transparent',
                    borderColor: 'transparent',
                    tooltip: 'Working Set - Click to change'
                };
        }
    };
    
    const config = getPurposeConfig();
    
    return (
        <Tooltip title={disabled ? '' : config.tooltip} arrow placement="top">
            <IconButton
                size="small"
                onClick={handleClick}
                disabled={disabled}
                sx={{
                    p: { xs: 0.25, sm: 0.5 },
                    minWidth: { xs: 24, sm: 28 },
                    minHeight: { xs: 24, sm: 28 },
                    color: config.color,
                    bgcolor: config.bgColor,
                    border: '1px solid',
                    borderColor: config.borderColor,
                    borderRadius: 1,
                    opacity: disabled ? 0.5 : (purpose === PURPOSE_WORKING ? 0.4 : 1),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: disabled ? config.bgColor : alpha(config.color, 0.2),
                        opacity: disabled ? 0.5 : 1,
                        transform: disabled ? 'none' : 'scale(1.1)',
                    }
                }}
            >
                {config.icon}
            </IconButton>
        </Tooltip>
    );
};

const normalizeType = (rawType, lookupTypes) => {
    if (!rawType) return 'UNKNOWN';
    const upperRaw = rawType.toUpperCase();
    if (lookupTypes.includes(upperRaw)) return upperRaw;
    
    const aliases = {
        'REPS': 'REPETITIONS',
        'LBS': 'WEIGHT',
        'KG': 'WEIGHT',
        'MIN': 'DURATION',
        'SEC': 'DURATION',
        'HR': 'HEART_RATE',
        'BPM': 'HEART_RATE',
        'MI': 'DISTANCE',
        'KM': 'DISTANCE',
        'DEG': 'INCLINE',
        'DEGREES': 'INCLINE'
    };
    
    if (aliases[upperRaw]) {
        return aliases[upperRaw];
    }
    return upperRaw;
};

/**
 * Parse measurement tags from text, including PURPOSE field.
 * A value of '*' indicates user-input required (no preset value).
 * PURPOSE can be: warmup, working, cooldown (case-insensitive)
 */
const parseMeasurementTag = (text) => {
    const regex = /\[M\s+([a-zA-Z0-9_]+)(?:\s+unit:([a-zA-Z0-9%]+))?\s*\]([\s\S]*?)\[\/M\]/gi;
    const matches = [];
    let match;
    let extractedPurpose = PURPOSE_WORKING; // Default purpose
    
    while ((match = regex.exec(text)) !== null) {
        const rawType = match[1];
        const rawUnit = match[2] || '';
        const rawVal = match[3].trim();
        
        // Check if this is a PURPOSE field
        if (rawType.toUpperCase() === 'PURPOSE') {
            const purposeVal = rawVal.toUpperCase();
            if (purposeVal === 'WARMUP' || purposeVal === 'WARM-UP' || purposeVal === 'WARM_UP') {
                extractedPurpose = PURPOSE_WARMUP;
            } else if (purposeVal === 'COOLDOWN' || purposeVal === 'COOL-DOWN' || purposeVal === 'COOL_DOWN') {
                extractedPurpose = PURPOSE_COOLDOWN;
            } else if (purposeVal === 'WORKING' || purposeVal === 'WORK') {
                extractedPurpose = PURPOSE_WORKING;
            }
            // Don't add PURPOSE to measurements array
            continue;
        }
        
        // Check if value is '*' (user-input placeholder)
        const isUserInput = rawVal === '*';
        const numVal = isUserInput ? 0 : parseFloat(rawVal);
        
        matches.push({
            rawType,
            unit: rawUnit,
            defaultValue: isNaN(numVal) ? 0 : numVal,
            userInput: isUserInput
        });
    }
    
    return { measurements: matches, purpose: extractedPurpose };
};

const getChildrenString = (c) => {
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) return c.map(getChildrenString).join('');
    if (React.isValidElement(c)) return getChildrenString(c.props.children);
    return '';
};

const SuggestedActivityCard = ({ children, messageId, suggestionIndex }) => {
    const theme = useTheme();
    const { resources } = useResources();
    const { sendMessage, sessionId } = useSession();
    const { lookups } = useLookups();
    const { preferences } = usePreferences();
    const { openResource } = useResourceOpener();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeField, setActiveField] = useState(null);

    const availableTypes = useMemo(() => {
        return lookups?.measurement_types ? Object.keys(lookups.measurement_types) : [];
    }, [lookups]);

    const { activityId, measurements, contentString, initialPurpose } = useMemo(() => {
        let foundId = null;
        let fullText = '';
        
        const rawContent = getChildrenString(children);

        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && child.props.id && child.props.tagType === 'A') {
                foundId = child.props.id;
            }
        });

        const { measurements: parsed, purpose: extractedPurpose } = parseMeasurementTag(rawContent);
        
        const processedMeasurements = parsed.map(m => {
            const type = normalizeType(m.rawType, availableTypes);
            let unit = m.unit;
            if (!unit) {
                unit = preferences?.preferred_units?.[type] 
                    || (lookups?.measurement_types?.[type]?.[0]) 
                    || '';
            }
            
            let label = m.rawType.toUpperCase();
            if (type === 'REPETITIONS') label = 'REPS';
            if (type === 'DURATION') label = 'TIME';

            return { 
                type, 
                label, 
                unit, 
                defaultValue: m.defaultValue,
                userInput: m.userInput || false
            };
        });

        return { 
            activityId: foundId, 
            measurements: processedMeasurements, 
            contentString: rawContent,
            initialPurpose: extractedPurpose
        };
    }, [children, availableTypes, lookups, preferences]);

    const uniqueCardId = useMemo(() => {
        return `${messageId}-${suggestionIndex}-${activityId}-${contentString}`;
    }, [messageId, activityId, contentString, suggestionIndex]);
    
    const messageLockId = useMemo(() => {
        return `msg-lock-${messageId}`;
    }, [messageId]);

    const [cardState, setCardState] = useWidgetState(uniqueCardId, {
        values: {},
        purpose: initialPurpose,
        editedFields: {},  // Track which fields the user has explicitly set
        initialized: false
    });
    
    const [lockedBy, setLockedBy] = useWidgetState(messageLockId, null);

    const { values: formValues, purpose: currentPurpose, editedFields = {} } = cardState;
    const isMyLog = lockedBy === uniqueCardId;
    const isLockedByOther = lockedBy !== null && lockedBy !== uniqueCardId;
    
    const isSuccess = isMyLog;
    const isDisabled = isLockedByOther;

    // Determine which fields are still unset placeholders
    // A field is a placeholder if it was marked userInput AND the user hasn't edited it yet
    const placeholderFields = useMemo(() => {
        const fields = {};
        measurements.forEach(m => {
            if (m.userInput && !editedFields[m.type]) {
                fields[m.type] = true;
            }
        });
        return fields;
    }, [measurements, editedFields]);

    const hasUnsetPlaceholders = useMemo(() => {
        return Object.keys(placeholderFields).length > 0;
    }, [placeholderFields]);

    useEffect(() => {
        if (!activityId) return;
        if (!cardState.initialized) {
            const initialValues = {};
            measurements.forEach(m => {
                initialValues[m.type] = m.defaultValue;
            });
            setCardState({
                values: initialValues,
                purpose: initialPurpose,
                editedFields: {},
                initialized: true
            });
        }
    }, [activityId, measurements, cardState.initialized, initialPurpose, setCardState]);

    const activityDef = useMemo(() => {
        if (!activityId || !resources) return null;
        return resources.find(r => String(r.id) === String(activityId));
    }, [activityId, resources]);

    const activeSession = useMemo(() => {
        // Filter for ALL active sessions first
        const activeSessions = resources.filter(r =>
            (r.type === 'workout_session' || r.resource_type === 'workout_session') &&
            r.status === 'IN_PROGRESS'
        );

        // 1. Priority: Find the session linked specifically to THIS chat conversation
        const sessionForCurrentChat = activeSessions.find(r => r.chat_session_id === sessionId);
        if (sessionForCurrentChat) return sessionForCurrentChat;

        // 2. Fallback: If no session matches this chat ID (rare, but possible if context was lost),
        // use the most recently created active session instead of the first one found.
        if (activeSessions.length > 0) {
            return activeSessions.sort((a, b) => 
                new Date(b.created_at || b.start_time) - new Date(a.created_at || a.start_time)
            )[0];
        }

        return null;
    }, [resources, sessionId]);

    const handleOpenPicker = (event, type) => {
        if (isSuccess || isDisabled) return;
        setAnchorEl(event.currentTarget);
        setActiveField(type);
    };

    const handleClosePicker = () => {
        setAnchorEl(null);
        setActiveField(null);
    };

    const handleValueChange = (newValue) => {
        if (activeField) {
            const newValues = {
                ...formValues,
                [activeField]: newValue
            };
            const newEditedFields = {
                ...editedFields,
                [activeField]: true
            };
            setCardState(prev => ({ 
                ...prev, 
                values: newValues,
                editedFields: newEditedFields
            }));
        }
    };

    const handlePurposeChange = (newPurpose) => {
        setCardState(prev => ({ ...prev, purpose: newPurpose }));
    };

    const handleNameClick = () => {
        if (activityId) {
            openResource(activityId, 'exercise_definition', { mode: 'modal' });
        }
    };

    const handleRecord = async () => {
        if (!activeSession) {
            setError("No workout session in progress.");
            return;
        }
        if (!activityId) {
            setError("Activity definition missing.");
            return;
        }
        if (hasUnsetPlaceholders) {
            const unsetLabels = measurements
                .filter(m => placeholderFields[m.type])
                .map(m => m.label.toLowerCase());
            setError(`Set your ${unsetLabels.join(' & ')} before logging.`);
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const actionMeasurements = measurements.map(m => ({
                type: m.type,
                value: parseFloat(formValues[m.type]) || 0,
                unit: m.unit || null
            }));

            const payload = {
                exercise_definition_id: activityId,
                name: activityDef ? activityDef.name : 'Logged Activity',
                actions: [
                    {
                        measurements: actionMeasurements,
                        purpose: currentPurpose || PURPOSE_WORKING
                    }
                ]
            };

            await addActivityToSession(activeSession.id, payload);
            setLockedBy(uniqueCardId);
            
            const summaryParts = actionMeasurements.map(m => 
                `${m.value} ${m.unit || ''}`.trim()
            ).join(' / ');
            const activityName = activityDef ? activityDef.name : 'Activity';
            const purposeLabel = currentPurpose !== PURPOSE_WORKING 
                ? ` (${currentPurpose.toLowerCase()})` 
                : '';
            
            sendMessage(`Manually logged: ${activityName}${purposeLabel}: ${summaryParts}`);
        } catch (err) {
            console.error("Failed to log suggested activity:", err);
            setError(err.message || "Failed to log.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const cardBgColor = useMemo(() => {
        if (isSuccess) return alpha(theme.palette.success.main, 0.05);
        if (isDisabled) return theme.palette.action.hover;
        // Subtle tint based on purpose
        if (currentPurpose === PURPOSE_WARMUP) return alpha(theme.palette.warning.main, 0.03);
        if (currentPurpose === PURPOSE_COOLDOWN) return alpha(theme.palette.info.main, 0.03);
        return alpha(theme.palette.primary.main, 0.03);
    }, [isSuccess, isDisabled, currentPurpose, theme]);

    const cardBorderColor = useMemo(() => {
        if (isSuccess) return 'success.main';
        if (isDisabled) return 'divider';
        if (currentPurpose === PURPOSE_WARMUP) return 'warning.main';
        if (currentPurpose === PURPOSE_COOLDOWN) return 'info.main';
        return 'primary.main';
    }, [isSuccess, isDisabled, currentPurpose]);

    // Tooltip for Log button when placeholders remain
    const logButtonTooltip = hasUnsetPlaceholders && !isSuccess && !isDisabled
        ? `Set ${measurements.filter(m => placeholderFields[m.type]).map(m => m.label.toLowerCase()).join(' & ')} first`
        : '';

    return (
        <>
        <Card variant="outlined" sx={{
            mt: 0.5,
            mb: 0.5,
            borderColor: cardBorderColor,
            borderStyle: 'solid',
            borderRadius: 2,
            bgcolor: cardBgColor,
            opacity: isDisabled ? 0.6 : 1,
            transition: 'all 0.3s ease',
            pointerEvents: isDisabled ? 'none' : 'auto',
        }}>
            <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                <Stack 
                    direction="row" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    spacing={{ xs: 1, sm: 1.5 }}
                    flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                >
                    {/* Activity Info */}
                    <Box sx={{
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 0.5, sm: 0.75 },
                        minWidth: 0,
                        flex: { xs: '1 1 100%', sm: 1 },
                    }}>
                        {/* Single Icon - toggles purpose when clicked */}
                        <Tooltip 
                            title={isSuccess ? 'Logged' : isDisabled ? '' : 
                                currentPurpose === PURPOSE_WARMUP ? 'Warmup - Click to change' :
                                currentPurpose === PURPOSE_COOLDOWN ? 'Cooldown - Click to change' :
                                'Working Set - Click to change'} 
                            arrow 
                            placement="top"
                        >
                            <Box 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDisabled && !isSuccess) {
                                        const currentIndex = PURPOSE_CYCLE.indexOf(currentPurpose || PURPOSE_WORKING);
                                        const nextIndex = (currentIndex + 1) % PURPOSE_CYCLE.length;
                                        handlePurposeChange(PURPOSE_CYCLE[nextIndex]);
                                    }
                                }}
                                sx={{ 
                                    p: { xs: 0.5, sm: 0.375 }, 
                                    borderRadius: 1,
                                    bgcolor: isSuccess 
                                        ? alpha(theme.palette.success.main, 0.1) 
                                        : isDisabled 
                                            ? 'action.selected'
                                            : currentPurpose === PURPOSE_WARMUP
                                                ? alpha(theme.palette.warning.main, 0.15)
                                                : currentPurpose === PURPOSE_COOLDOWN
                                                    ? alpha(theme.palette.info.main, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.1),
                                    color: isSuccess 
                                        ? 'success.main' 
                                        : isDisabled 
                                            ? 'text.disabled'
                                            : currentPurpose === PURPOSE_WARMUP
                                                ? 'warning.main'
                                                : currentPurpose === PURPOSE_COOLDOWN
                                                    ? 'info.main'
                                                    : 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: (!isDisabled && !isSuccess) ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': (!isDisabled && !isSuccess) ? {
                                        transform: 'scale(1.1)',
                                        bgcolor: currentPurpose === PURPOSE_WARMUP
                                            ? alpha(theme.palette.warning.main, 0.25)
                                            : currentPurpose === PURPOSE_COOLDOWN
                                                ? alpha(theme.palette.info.main, 0.25)
                                                : alpha(theme.palette.primary.main, 0.2),
                                    } : undefined
                                }}
                            >
                                {isSuccess ? <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> : 
                                 isDisabled ? <BlockIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> :
                                 currentPurpose === PURPOSE_WARMUP ? <LocalFireDepartmentIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> :
                                 currentPurpose === PURPOSE_COOLDOWN ? <AcUnitIcon sx={{ fontSize: { xs: 18, sm: 20 } }} /> :
                                 <FitnessCenterIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            </Box>
                        </Tooltip>
                        <Typography 
                            variant="subtitle2" 
                            fontWeight="bold" 
                            noWrap 
                            onClick={isDisabled ? undefined : handleNameClick}
                            sx={{
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                lineHeight: 1.2,
                                color: isDisabled ? 'text.disabled' : 'text.primary',
                                cursor: (!isDisabled) ? 'pointer' : 'default',
                                '&:hover': (!isDisabled) ? { color: 'primary.main', textDecoration: 'underline' } : undefined
                            }}
                        >
                            {activityDef ? activityDef.name : (activityId || "Activity")}
                        </Typography>
                    </Box>

                    {/* Measurement Pickers */}
                    <Stack 
                        direction="row" 
                        spacing={0.5} 
                        useFlexGap 
                        flexWrap="wrap"
                        sx={{ 
                            flex: { xs: '1 1 100%', sm: 'none' },
                            justifyContent: { xs: 'flex-start', sm: 'center' },
                            mt: { xs: 0.75, sm: 0 },
                            rowGap: 0.5,
                            opacity: (isSuccess || isDisabled) ? 0.7 : 1,
                            pointerEvents: (isSuccess || isDisabled) ? 'none' : 'auto'
                        }}
                    >
                        {measurements.map((m) => (
                            <SpinWheelTrigger
                                key={m.type}
                                label={m.label}
                                value={formValues[m.type]}
                                unit={m.unit}
                                onClick={(e) => handleOpenPicker(e, m.type)}
                                disabled={isDisabled || isSuccess}
                                isPlaceholder={!!placeholderFields[m.type]}
                            />
                        ))}
                    </Stack>

                    {/* Log Button (Desktop) */}
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Tooltip title={logButtonTooltip} arrow placement="top">
                            <span> {/* span wrapper needed for Tooltip on disabled button */}
                                <Button
                                    variant={isSuccess ? "outlined" : (isDisabled ? "text" : "contained")}
                                    color={isSuccess ? "success" : (isDisabled ? "inherit" : 
                                        currentPurpose === PURPOSE_WARMUP ? "warning" :
                                        currentPurpose === PURPOSE_COOLDOWN ? "info" : "primary")}
                                    size="small"
                                    onClick={handleRecord}
                                    disabled={isSubmitting || isDisabled || !activeSession || isSuccess || hasUnsetPlaceholders}
                                    sx={{ 
                                        minWidth: '60px', 
                                        height: '32px',
                                        borderRadius: 2,
                                        boxShadow: (isSuccess || isDisabled || hasUnsetPlaceholders) ? 0 : 2,
                                    }}
                                >
                                    {isSubmitting ? <CircularProgress size={16} color="inherit" /> : 
                                     isSuccess ? "Logged" : 
                                     isDisabled ? "Option Ignored" : "Log"}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Stack>

                {/* Log Button (Mobile) */}
                <Tooltip title={logButtonTooltip} arrow placement="top">
                    <span>
                        <Button
                            variant={isSuccess ? "outlined" : (isDisabled ? "text" : "contained")}
                            color={isSuccess ? "success" : (isDisabled ? "inherit" : 
                                currentPurpose === PURPOSE_WARMUP ? "warning" :
                                currentPurpose === PURPOSE_COOLDOWN ? "info" : "primary")}
                            size="small"
                            onClick={handleRecord}
                            disabled={isSubmitting || isDisabled || !activeSession || isSuccess || hasUnsetPlaceholders}
                            fullWidth
                            sx={{ 
                                display: { xs: 'flex', sm: 'none' }, 
                                height: '26px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                boxShadow: (isSuccess || isDisabled || hasUnsetPlaceholders) ? 0 : 1,
                                mt: 0.75
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={14} color="inherit" /> : 
                             isSuccess ? "Logged" : 
                             isDisabled ? "Option Ignored" : "Log"}
                        </Button>
                    </span>
                </Tooltip>

                {error && (
                    <Alert severity="error" sx={{ mt: 1, py: 0, px: 1, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
                        {error}
                    </Alert>
                )}

                <SpinWheelPicker
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClosePicker}
                    value={activeField ? parseFloat(formValues[activeField]) : 0}
                    onChange={handleValueChange}
                    values={activeField ? generateOptions(activeField) : []}
                    itemHeight={36} // Use 36 for compact view
                />

            </CardContent>
        </Card>
        {/* Reminder to adjust values */}
        {!isSuccess && !isDisabled && (
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: 'block',
                    mt: 0.25,
                    mb: 0.25,
                    fontSize: '0.6rem',
                    fontStyle: 'italic',
                    opacity: 0.75,
                    textAlign: 'center',
                }}
            >
                Suggestion only — make adjustments as needed.
            </Typography>
        )}
    </>
    );
};

export default memo(SuggestedActivityCard);